import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { easeOut } from "@/components/motion";
import { AppStoreBadge, GooglePlayBadge } from "@/components/StoreBadges";

const HERO_IMG =
  "https://images.unsplash.com/photo-1637430308606-86576d8fef3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative h-[100svh] w-full overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={HERO_IMG} alt="Athletes training in a gym" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-[#0A0A0A]"
      />

      {/* Kinetic side label */}
      <div className="pointer-events-none absolute right-4 top-1/2 hidden -translate-y-1/2 rotate-90 md:block">
        <span className="font-condensed text-sm tracking-[0.5em] text-white/40">EST. 2026 — TRAIN BETTER. TOGETHER</span>
      </div>

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-20 md:px-12 md:pb-28"
      >
        <h1 className="font-display text-[15vw] leading-[0.85] uppercase sm:text-[13vw] md:text-[9.5vw] lg:text-[8.5vw]">
          <span data-testid="hero-headline-primary" className="block">Find your perfect</span>
          <span className="block">
            <span data-testid="hero-headline-accent" className="hero-accent inline-block origin-left">gym partner</span>
          </span>
          <span data-testid="hero-glowbar" className="mt-3 block h-1 w-[42%] max-w-md hero-glowbar" />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: easeOut, delay: 1.3 }}
          className="mt-6 max-w-xl text-base text-zinc-300 md:text-lg"
        >
          Trainova is the swipe-based app that matches you with the perfect gym partner — by
          goals, experience level, location and more. Swipe, match, and build a routine that
          actually lasts.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: easeOut, delay: 1.5 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <GooglePlayBadge />
          <AppStoreBadge />
        </motion.div>

        <motion.div
          data-testid="hero-crunch-partner"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: easeOut, delay: 1.7 }}
          className="mt-10 flex w-fit items-center gap-4"
        >
          <div className="relative">
            <motion.span
              className="absolute inset-0 -z-10 rounded-full blur-xl"
              style={{ background: "radial-gradient(circle, rgba(18,224,201,0.65), transparent 70%)" }}
              animate={{ opacity: [0.35, 0.9, 0.35], scale: [0.9, 1.05, 0.9] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
            />
            <img src="/crunch-logo.png" alt="Crunch Fitness" className="h-14 w-20 rounded-md object-cover" />
          </div>
          <div className="leading-tight">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-teal">Partnered &amp; backed by</div>
            <div className="font-condensed text-3xl tracking-wide text-white md:text-4xl">CRUNCH FITNESS</div>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2 text-white/50"
        >
          <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
}
